/**
 * Employer-facing remediation copy per verification reason code (contract 7.1).
 *
 * ONE SENTENCE PER CODE, AND IT SAYS WHAT TO DO. A reason code on its own is an
 * internal label; a recruiter reading "FACILITY_UNVERIFIED" learns nothing they
 * can act on. Every line here names the next move.
 *
 * MIRRORS `organization-verification/verification-reason-codes.ts`. The server
 * is the source of the codes and this file is the source of the employer-facing
 * words, so one grep on a code name finds both halves the day either moves.
 *
 * SHARED BY THREE SURFACES on purpose: the verification form in Settings,
 * `ReviewStatusCard`, and `UnderReviewModal` all say the same thing about the
 * same code. Three copies of these nine sentences would drift the first time
 * one was reworded.
 */
export const VERIFICATION_REASON_CODES = [
  'DUPLICATE_ORGANIZATION',
  'IDENTITY_UNCONFIRMED',
  'NOT_AN_ANESTHESIA_EMPLOYER',
  'FACILITY_UNVERIFIED',
  'CONTACT_UNREACHABLE',
  'POLICY_VIOLATION',
  'FRAUD_SUSPECTED',
  'PROVIDER_COMPLAINT',
  'ADMIN_OTHER',
];

export const VERIFICATION_REASON_COPY = {
  DUPLICATE_ORGANIZATION:
    'This organization already has an Rx Rooster account. Sign in to that account, or contact support to merge them.',
  IDENTITY_UNCONFIRMED:
    'We could not confirm who is authorized to post for this organization. Add a work email and the name and title of the person authorized to post, then resubmit.',
  NOT_AN_ANESTHESIA_EMPLOYER:
    'Rx Rooster verifies organizations that employ or place anesthesia providers. If that describes you, tell us more in your business details.',
  FACILITY_UNVERIFIED:
    'We could not match a facility to a national provider record. Add a facility with its NPI, then resubmit.',
  CONTACT_UNREACHABLE:
    'We could not reach the contact on file. Update your contact email and phone, then resubmit.',
  POLICY_VIOLATION:
    'This account was paused for a policy concern. Reply to the notice we sent to discuss next steps.',
  FRAUD_SUSPECTED:
    'This account is paused while we review activity on it. Reply to the notice we sent to discuss next steps.',
  PROVIDER_COMPLAINT:
    'This account is paused while we review a provider report. Reply to the notice we sent to discuss next steps.',
  ADMIN_OTHER:
    'Our team needs more information before this account can continue. Reply to the notice we sent.',
};

/**
 * The sentence for one code.
 *
 * AN UNKNOWN OR ABSENT CODE FALLS BACK TO `ADMIN_OTHER`, never to silence. A
 * suspension with no reason on it is still a suspension the recruiter has to be
 * told how to answer, and a blank panel would read as a rendering bug.
 */
export const verificationReasonCopy = (code) =>
  VERIFICATION_REASON_COPY[code] || VERIFICATION_REASON_COPY.ADMIN_OTHER;

export default VERIFICATION_REASON_COPY;

/**
 * The five codes `InvalidateConversationDto` accepts (contract 7.1).
 *
 * A separate list from the verification codes on purpose. Voiding a confirmed
 * conversation is not the same judgement as pausing an organization, and an
 * admin offered all fourteen in one select would reach for the wrong half.
 * These are internal labels, never shown to an employer, so there is no
 * remediation copy keyed off them.
 */
export const CONVERSATION_INVALIDATION_REASON_CODES = [
  'DUPLICATE_ACCOUNT',
  'SELF_DEALING',
  'FABRICATED_CONFIRMATION',
  'PROVIDER_REQUEST',
  'ADMIN_OTHER',
];
